import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { qrRows } from '../../lib/crewWeb'
import { useLayer } from '../../components/layers'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { ClientsResponse, dateText, TagRow, TagsResponse, TagType } from './api'
import { CoMingling } from './CoMingling'
import styles from './GodMode.module.css'

// Every tag a client has scanned, printed, been given as worker cards or imported from the old system, or Dataphyll's
// own stock: filtered by type, searched by Display ID, code or the person a worker card is for. Ticked tags can be
// given their own tare and full weight (Crate knows Weight) and exported as a spreadsheet; each shows its QR. An item
// tag that is on a legacy bin can be marked as a bin (Dataphyll Crew then treats it as one). Co-mingling, clients
// sharing their tags, is set at the top (CoMingling.tsx).

export const STOCK = 'stock'
// Tags shown a page at a time; Select all still ticks every tag that matches.
export const PAGE_SIZE = 50
const TYPE_LABEL: Record<TagType, string> = { item: 'Item', bin: 'Bin', rack: 'Rack', card: 'Worker card' }

// Grams shown as kg: 4100 → "4.1".
export const kgText = (g: number | null | undefined) => (g == null ? '' : String(Math.round(g) / 1000))

// A kg box as grams: blank → undefined, else a whole number of grams, or NaN when it is not a number.
export const kgToGrams = (v: string) => (v.trim() === '' ? undefined : Number(v) >= 0 ? Math.round(Number(v) * 1000) : NaN)

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// An item tag (DT…, not a worker's DTU…) may be marked as a bin; one marked may be unmarked.
export const canMarkBin = (t: TagRow) => t.marked_bin || (t.type === 'item' && /^DT(?!U)/.test(t.code))

export function tagsCsv(rows: TagRow[]) {
  const head = ['Display ID', 'Code', 'Type', 'Person', 'First seen', 'Scanned', 'Printed', 'Imported', 'Tare (kg)', 'Full (kg)']
  const lines = rows.map((t) =>
    [
      t.display_id,
      t.code,
      t.marked_bin ? 'Bin (item tag)' : TYPE_LABEL[t.type],
      t.person ?? '',
      t.first_at ? t.first_at.slice(0, 10) : '',
      t.seen ? 'yes' : '',
      t.printed ? 'yes' : '',
      t.imported ? 'yes' : '',
      kgText(t.tare_g),
      kgText(t.full_g),
    ]
      .map(csvCell)
      .join(','),
  )
  return [head.join(','), ...lines].join('\r\n') + '\r\n'
}

function where(t: TagRow) {
  if (t.note !== undefined) return t.note ? `Stock · ${t.note}` : 'Stock'
  return [t.seen && 'Scanned', t.printed && 'Printed', t.imported && 'Imported'].filter(Boolean).join(', ') || 'Weights only'
}

function QrDialog({ tag, onClose }: { tag: TagRow; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null)
  const { requestClose } = useLayer(true, onClose, { el: box, modal: true, focusFirst: true })
  const rows = useMemo(() => qrRows(tag.code), [tag.code])
  const n = rows.length
  return (
    <div
      role="dialog"
      aria-label={`QR for ${tag.display_id}`}
      onClick={requestClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', zIndex: 50 }}
    >
      <div ref={box} className={styles.card} onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', maxWidth: 360 }}>
        <h2>{tag.display_id}</h2>
        <svg viewBox={`-4 -4 ${n + 8} ${n + 8}`} width={260} height={260} role="img" aria-label={`QR code ${tag.code}`} shapeRendering="crispEdges">
          <rect x={-4} y={-4} width={n + 8} height={n + 8} fill="#fff" />
          {rows.map((row, r) =>
            [...row.matchAll(/1+/g)].map((m) => <rect key={`${r}-${m.index}`} x={m.index} y={r} width={m[0].length} height={1} fill="#000" />),
          )}
        </svg>
        <p className={styles.hint}>
          <code>{tag.code}</code>
        </p>
        <button type="button" className={styles.secondary} onClick={requestClose}>
          Close
        </button>
      </div>
    </div>
  )
}

export function TagsPage() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const source = params.get('client') || ''
  const [clients, setClients] = useState<{ id: string; name: string }[] | null>(null)
  const [type, setType] = useState<TagType | ''>('')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [data, setData] = useState<TagsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [chosen, setChosen] = useState<string[]>([])
  const [tare, setTare] = useState('')
  const [full, setFull] = useState('')
  const [clearTare, setClearTare] = useState(false)
  const [clearFull, setClearFull] = useState(false)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [qr, setQr] = useState<TagRow | null>(null)
  const [page, setPage] = useState(0)
  const stock = source === STOCK

  useEffect(() => {
    apiJson<ClientsResponse>(user, '/api/god-mode/clients')
      .then((r) => setClients(r.clients.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name))))
      .catch((e) => setError(e.message))
  }, [user])

  // The search is asked of the API a moment after typing stops.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  const load = useCallback(async () => {
    if (!source) {
      setData(null)
      return
    }
    const qs = new URLSearchParams()
    if (type) qs.set('type', type)
    if (q) qs.set('q', q)
    const path = stock ? '/api/god-mode/stock-tags' : `/api/god-mode/clients/${encodeURIComponent(source)}/tags`
    try {
      setError(null)
      setData(await apiJson<TagsResponse>(user, `${path}${qs.size ? `?${qs}` : ''}`))
    } catch (e: any) {
      setError(e.message)
    }
  }, [user, source, stock, type, q])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    setChosen([])
    setSaved(null)
  }, [source])

  // A new filter or search starts again at its first page.
  useEffect(() => setPage(0), [source, type, q])

  // Every tag that matches (data.tags), and the page of them shown.
  const tags = useMemo(() => data?.tags ?? [], [data])
  const pages = Math.max(1, Math.ceil(tags.length / PAGE_SIZE))
  const at = Math.min(page, pages - 1)
  const shown = tags.slice(at * PAGE_SIZE, (at + 1) * PAGE_SIZE)
  const chosenSet = useMemo(() => new Set(chosen), [chosen])
  const matchCodes = useMemo(() => tags.map((t) => t.code), [tags])
  const ticked = useMemo(() => matchCodes.filter((c) => chosenSet.has(c)), [matchCodes, chosenSet])
  const pageCodes = shown.map((t) => t.code)
  const allOnPage = shown.length > 0 && shown.every((t) => chosenSet.has(t.code))
  const toggle = (codes: string[], on: boolean) =>
    setChosen((was) => {
      if (!on) {
        const off = new Set(codes)
        return was.filter((x) => !off.has(x))
      }
      const had = new Set(was)
      return [...was, ...codes.filter((x) => !had.has(x))]
    })

  const tareG = clearTare ? null : kgToGrams(tare)
  const fullG = clearFull ? null : kgToGrams(full)
  const badWeight = Number.isNaN(tareG) || Number.isNaN(fullG) || fullG === 0
  const nothingToSet = tareG === undefined && fullG === undefined

  const setWeights = async (e: React.FormEvent) => {
    e.preventDefault()
    if (stock || !ticked.length || badWeight || nothingToSet) return
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      const body: Record<string, unknown> = { codes: ticked }
      if (tareG !== undefined) body.tare_g = tareG
      if (fullG !== undefined) body.full_g = fullG
      const r = await apiJson<{ changed: number; reestimated: number }>(user, `/api/god-mode/clients/${encodeURIComponent(source)}/tags/weights`, {
        method: 'PUT',
        body,
      })
      setSaved(
        `Weights set on ${r.changed} tag${r.changed === 1 ? '' : 's'}.` +
          (r.reestimated ? ` ${r.reestimated} shared bin${r.reestimated === 1 ? '' : 's'} not yet weighed estimated again.` : ''),
      )
      setTare('')
      setFull('')
      setClearTare(false)
      setClearFull(false)
      await load()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  // Mark the ticked item tags as bins (legacy bins with item tags), or unmark ticked marked ones.
  const markable = tags.filter((t) => chosenSet.has(t.code) && canMarkBin(t))
  const toMark = markable.filter((t) => !t.marked_bin).map((t) => t.code)
  const toUnmark = markable.filter((t) => t.marked_bin).map((t) => t.code)
  const setBins = async (codes: string[], isBin: boolean) => {
    if (stock || !codes.length) return
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      const r = await apiJson<{ changed: number }>(user, `/api/god-mode/clients/${encodeURIComponent(source)}/tags/bins`, {
        method: 'PUT',
        body: { codes, is_bin: isBin },
      })
      setSaved(`${r.changed} tag${r.changed === 1 ? '' : 's'} ${isBin ? 'marked as a bin' : 'no longer a bin'}. Dataphyll Crew picks this up at its next sync.`)
      await load()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  // The ticked tags, or every one that matches when none is ticked.
  const exportCsv = () => {
    const rows = ticked.length ? tags.filter((t) => chosenSet.has(t.code)) : tags
    const blob = new Blob([tagsCsv(rows)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const who = stock ? 'stock' : (data?.client?.name || 'client').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase()
    a.download = `dataphyll-tags-${who}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <PageHeader title={data?.client ? `Tags · ${data.client.name}` : stock ? 'Tags · Dataphyll stock' : 'Tags'} />
      <PageBody>
        <div className={styles.stack}>
          <CoMingling />
          <div className={`${styles.card} ${styles.inline}`}>
            <label className={styles.field}>
              Client
              <select
                value={source}
                onChange={(e) => setParams(e.target.value ? { client: e.target.value } : {}, { replace: true })}
                aria-label="Client"
              >
                <option value="">Choose a client…</option>
                <option value={STOCK}>Dataphyll stock (no client yet)</option>
                {(clients ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              Type
              <select value={type} onChange={(e) => setType(e.target.value as TagType | '')} aria-label="Type">
                <option value="">All types</option>
                {(Object.keys(TYPE_LABEL) as TagType[]).map((k) => (
                  <option key={k} value={k}>
                    {TYPE_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              Search
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Display ID, code or person" aria-label="Search" />
            </label>
          </div>

          {error && <p className={styles.error}>{error}</p>}
          {!source && <p className={styles.lead}>Choose a client, or Dataphyll stock, to list its tags.</p>}
          {source && !data && !error && <p className={styles.lead}>Loading…</p>}

          {data && (
            <>
              {!stock && (
                <form className={`${styles.card} ${styles.form}`} onSubmit={setWeights} aria-label="Set weights">
                  <h2>Weights of the ticked tags</h2>
                  {saved && <p className={styles.saved}>{saved}</p>}
                  <div className={styles.inline}>
                    <label className={styles.field}>
                      Tare (kg)
                      <input
                        type="number"
                        min={0}
                        max={2000}
                        step="any"
                        value={tare}
                        disabled={clearTare}
                        onChange={(e) => setTare(e.target.value)}
                        placeholder="Keep"
                      />
                    </label>
                    <label>
                      <input type="checkbox" checked={clearTare} onChange={(e) => setClearTare(e.target.checked)} /> Clear tare
                    </label>
                    <label className={styles.field}>
                      Full weight (kg)
                      <input
                        type="number"
                        min={0}
                        max={5000}
                        step="any"
                        value={full}
                        disabled={clearFull}
                        onChange={(e) => setFull(e.target.value)}
                        placeholder="Keep"
                      />
                    </label>
                    <label>
                      <input type="checkbox" checked={clearFull} onChange={(e) => setClearFull(e.target.checked)} /> Clear full weight
                    </label>
                  </div>
                  <span className={styles.hint}>
                    Blank keeps what a tag has. Tare is the empty item or bin; full weight is the fruit it holds when full, which a shared bin in
                    Self Scan is taken to hold until it is weighed (else the Site's estimated bin weight).
                  </span>
                  {badWeight && <p className={styles.error}>Weights are kg: tare up to 2000, full weight more than 0 and up to 5000.</p>}
                  <div className={styles.inline}>
                    <button type="submit" className={styles.primary} disabled={busy || !ticked.length || badWeight || nothingToSet}>
                      Set on {ticked.length} tag{ticked.length === 1 ? '' : 's'}
                    </button>
                    <button type="button" className={styles.secondary} disabled={busy || !toMark.length} onClick={() => setBins(toMark, true)}>
                      Mark {toMark.length || ''} as bin{toMark.length === 1 ? '' : 's'}
                    </button>
                    <button type="button" className={styles.secondary} disabled={busy || !toUnmark.length} onClick={() => setBins(toUnmark, false)}>
                      Not a bin{toUnmark.length ? ` (${toUnmark.length})` : ''}
                    </button>
                  </div>
                  <span className={styles.hint}>
                    Mark as bin: an item tag that is on a legacy bin. It is then listed as a bin, and Dataphyll Crew here, and at every client this
                    one shares its tags with, treats it as a bin.
                  </span>
                </form>
              )}

              <div className={styles.inline}>
                <span className={styles.lead}>
                  {data.total > tags.length
                    ? `${tags.length} of ${data.total} tags (narrow the search for the rest).`
                    : `${data.total} tag${data.total === 1 ? '' : 's'}.`}
                  {ticked.length > 0 && ` ${ticked.length} ticked.`}
                </span>
                <button type="button" className={styles.secondary} onClick={() => toggle(matchCodes, true)} disabled={!tags.length}>
                  Select all{tags.length > shown.length ? ` ${tags.length}` : ''}
                </button>
                <button type="button" className={styles.secondary} onClick={() => setChosen([])} disabled={!chosen.length}>
                  Select none
                </button>
                <button type="button" className={styles.secondary} onClick={exportCsv} disabled={!tags.length}>
                  Export spreadsheet{ticked.length ? ` (${ticked.length})` : ''}
                </button>
              </div>

              {tags.length === 0 ? (
                <p className={styles.empty}>No tags match.</p>
              ) : (
                <div className={styles.tableCard}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>
                          <input type="checkbox" aria-label="Tick every tag on this page" checked={allOnPage} onChange={(e) => toggle(pageCodes, e.target.checked)} />
                        </th>
                        <th>Display ID</th>
                        <th>Type</th>
                        <th>Person</th>
                        <th>Where from</th>
                        <th>First</th>
                        {!stock && <th>Tare (kg)</th>}
                        {!stock && <th>Full (kg)</th>}
                        <th>QR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((t) => (
                        <tr key={t.code}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Tick ${t.display_id}`}
                              checked={chosenSet.has(t.code)}
                              onChange={(e) => toggle([t.code], e.target.checked)}
                            />
                          </td>
                          <td title={t.code}>{t.display_id}</td>
                          <td>{t.marked_bin ? <span title="An item tag marked as a bin">Bin (item tag)</span> : TYPE_LABEL[t.type]}</td>
                          <td>{t.person ?? ''}</td>
                          <td>{where(t)}</td>
                          <td>{dateText(t.first_at)}</td>
                          {!stock && <td>{kgText(t.tare_g)}</td>}
                          {!stock && <td>{kgText(t.full_g)}</td>}
                          <td>
                            <button type="button" className={styles.secondary} onClick={() => setQr(t)} aria-label={`Show QR for ${t.display_id}`}>
                              QR
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {pages > 1 && (
                    <div className={styles.inline} aria-label="Pages">
                      <button type="button" className={styles.secondary} onClick={() => setPage(at - 1)} disabled={at === 0}>
                        Previous
                      </button>
                      <span className={styles.hint}>
                        Page {at + 1} of {pages} · {at * PAGE_SIZE + 1}–{at * PAGE_SIZE + shown.length} of {tags.length}
                      </span>
                      <button type="button" className={styles.secondary} onClick={() => setPage(at + 1)} disabled={at >= pages - 1}>
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </PageBody>
      {qr && <QrDialog tag={qr} onClose={() => setQr(null)} />}
    </>
  )
}
